import {
	type BlockingPeriod,
	type SaveBlockingPeriodRequest,
	apiAppBusinessBlockingperiodDelete,
	apiAppBusinessBlockingperiodSearch,
	apiAppBusinessBlockingperiodUpsert
} from '#lib/types/generated/index.js';
import { optimisticDelete, optimisticSave } from '#lib/stores/optimistic.js';
import { DateTime } from 'luxon';
import { get, writable } from 'svelte/store';
import { businessStore } from './business';
import { calendarDateRange } from './calendarDates';

export const blockingPeriods = writable<BlockingPeriod[]>([]);

let startDateLoaded: DateTime | null = null;
let endDateLoaded: DateTime | null = null;

export function initBlockingPeriods(items: BlockingPeriod[], start: DateTime, end: DateTime): void {
	startDateLoaded = start;
	endDateLoaded = end;
	blockingPeriods.set(items);
}

export function clearBlockingPeriods(): void {
	startDateLoaded = null;
	endDateLoaded = null;
	blockingPeriods.set([]);
}

export function loadBlockingPeriods(
	startDate: DateTime | undefined = undefined,
	endDate: DateTime | undefined = undefined
): Promise<void> {
	if (!get(businessStore).id) return Promise.resolve();
	const start =
		startDate ??
		(startDateLoaded
			? DateTime.min(get(calendarDateRange).start, startDateLoaded)
			: get(calendarDateRange).start);
	const end =
		endDate ??
		(endDateLoaded
			? DateTime.max(get(calendarDateRange).end, endDateLoaded)
			: get(calendarDateRange).end);
	return apiAppBusinessBlockingperiodSearch({
		startDate: start.toISO(),
		endDate: end.toISO()
	}).then((res) => {
		startDateLoaded = start;
		endDateLoaded = end;
		blockingPeriods.set(res.items);
	});
}

calendarDateRange.subscribe((range) => {
	if (!get(businessStore).id) return;
	const startIso = range.start.toISODate();
	const endIso = range.end.toISODate();
	const loadedStartIso = startDateLoaded?.toISODate();
	const loadedEndIso = endDateLoaded?.toISODate();

	if (
		startIso &&
		endIso &&
		loadedStartIso &&
		loadedEndIso &&
		startIso >= loadedStartIso &&
		endIso <= loadedEndIso
	) {
		return;
	}
	loadBlockingPeriods();
});

export function saveBlockingPeriod(blockingPeriod: BlockingPeriod): Promise<BlockingPeriod> {
	const payload: SaveBlockingPeriodRequest = {
		date: blockingPeriod.date.toISODate()!,
		start: blockingPeriod.start,
		end: blockingPeriod.end,
		areaId: blockingPeriod.areaId
	};

	return optimisticSave(blockingPeriods, blockingPeriod, () =>
		apiAppBusinessBlockingperiodUpsert(blockingPeriod.id, payload)
	);
}

export function deleteBlockingPeriod(id: string): Promise<void> {
	return optimisticDelete(blockingPeriods, id, () => apiAppBusinessBlockingperiodDelete(id));
}
